import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import KycUploadModal from "./KycUploadModal";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUserKycList,
  uploadUserKyc,
} from "../../../features/user/userSlice";
import AppMessage from "../../common/AppMessage";
import Loader from "../../common/Loader";
import { timeAgo } from "../../../utils/timeAgo";
import { capitalizeFirst } from "../../../utils/stringUtils";

// ─── Flatten each KYC record into individual document rows ───────────────────
// The API returns document_type1, document_type2, document_url1, document_url2
// on a single record. This expands each into separate display rows.
const flattenKycDocuments = (records = []) => {
  const rows = [];

  records.forEach((doc) => {
    // Collect all numbered slots (1, 2, 3 … in case the API ever returns more)
    const maxSlots = 3; // adjust if the API can return more than 2
    for (let i = 1; i <= maxSlots; i++) {
      const docType = doc[`document_type${i}`];
      const docUrl = doc[`document_url${i}`];
      const docPath = doc[`document_path${i}`];

      if (docType || docUrl) {
        rows.push({
          id: `${doc.id}-${i}`, // unique key per row
          parentId: doc.id,
          documentType: docType || "—",
          documentUrl:
            docUrl ||
            (docPath ? `https://scraptrade.com.au/storage/${docPath}` : null),
          status: doc.status,
          created_at: doc.created_at,
          kycType: doc.kyc_type,
        });
      }
    }
  });

  return rows;
};

const KycVerificationTab = () => {
  const dispatch = useDispatch();
  const { kycList: documents = [], kycLoading } = useSelector(
    (state) => state.user,
  );
  const warning = useSelector((state) => state.ui.kycWarningMessage);

  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [openKYCModal, setOpenKYCModal] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    dispatch(fetchUserKycList());
  }, [dispatch]);

  // ── Flatten API records into individual rows ──────────────────────────────
  const flatDocs = flattenKycDocuments(documents);

  // ── Filter across the flattened rows ─────────────────────────────────────
  const filteredDocs = flatDocs.filter(
    (row) =>
      (row.documentType || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (row.kycType || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (row.status || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const startIndex = (page - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentDocs = filteredDocs.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredDocs.length / entriesPerPage);

  const getStatusChipColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const handleSaveKYC = async (data) => {
    try {
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("is_consent", data.isConsent ? 1 : 0);

      data.documents.forEach((doc, index) => {
        formData.append(`kyc[${index}][kycType]`, data.kycType);
        formData.append(`kyc[${index}][documentType]`, doc.type);
        formData.append(`kyc[${index}][file]`, doc.file);
      });

      await dispatch(uploadUserKyc(formData)).unwrap();
      dispatch(fetchUserKycList());
      setOpenKYCModal(false);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const CustomPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Button
          key={i}
          variant={page === i ? "contained" : "outlined"}
          size="small"
          onClick={() => setPage(i)}
          sx={{
            minWidth: { xs: 28, sm: 36 },
            height: { xs: 28, sm: 32 },
            mx: { xs: 0.15, sm: 0.3 },
            fontSize: { xs: "0.65rem", sm: "0.75rem" },
            fontWeight: "bold",
            borderRadius: "4px",
            bgcolor: page === i ? "grey.300" : "#fff",
            border: "1px solid #ccc",
            color: "#000",
            "&:hover": { bgcolor: page === i ? "grey.400" : "grey.100" },
          }}
        >
          {i}
        </Button>,
      );
    }
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 0.5, sm: 1 },
          mt: 1,
        }}
      >
        <Button
          size="small"
          onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          disabled={page === 1}
          sx={{
            fontSize: { xs: "0.65rem", sm: "0.75rem" },
            border: "1px solid #ccc",
            borderRadius: "4px",
            height: { xs: 28, sm: 32 },
            minWidth: { xs: 40, sm: 60 },
            bgcolor: "#fff",
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          Prev
        </Button>
        {pages}
        <Button
          size="small"
          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={page === totalPages}
          sx={{
            fontSize: { xs: "0.65rem", sm: "0.75rem" },
            border: "1px solid #ccc",
            borderRadius: "4px",
            height: { xs: 28, sm: 32 },
            minWidth: { xs: 40, sm: 60 },
            bgcolor: "#fff",
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          Next
        </Button>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      {warning && (
        <AppMessage
          type="error"
          message="Complete your KYC for seamless access."
          style={{ marginBottom: 12 }}
        />
      )}

      <Typography
        variant={isMobile ? "h5" : "h4"}
        sx={{ fontWeight: "bold", mb: 1 }}
      >
        KYC Verification
      </Typography>
      <Typography
        variant={isMobile ? "body1" : "h6"}
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Please upload your documents here to complete your Profile Verification
        (Company Registration – ABN/ACN, SLED – Scrap Trader Registration
        Certificate, Driver's License, Address Proof – Utility Bill, Bank
        Statement, or Credit Card Statement).
      </Typography>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Uploaded Documents
        </Typography>
        <Button
          variant="contained"
          sx={{
            bgcolor: "scraptradeYellow.main",
            "&:hover": { bgcolor: "scraptradeYellow.dark" },
          }}
          onClick={() => setOpenKYCModal(true)}
        >
          Upload KYC
        </Button>
      </Box>

      {kycLoading && <Loader />}

      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 2,
        }}
      >
        <FormControl sx={{ minWidth: { xs: "100%", sm: 150 } }}>
          <InputLabel>Entries per page</InputLabel>
          <Select
            value={entriesPerPage}
            onChange={(e) => {
              setEntriesPerPage(e.target.value);
              setPage(1);
            }}
            label="Entries per page"
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
          </Select>
        </FormControl>

        <TextField
          placeholder="Search documents..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
        />
      </Box>

      {/* Table */}
      <Box sx={{ width: "100%", overflowX: "auto", padding: 1 }}>
        <TableContainer component={Paper}>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                {[
                  "Sr. No",
                  "Verification Type",
                  "Document Type",
                  "File",
                  "Status",
                  "Uploaded",
                ].map((header, index) => (
                  <TableCell key={index} sx={{ fontWeight: "bold" }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentDocs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 4, color: "text.secondary", fontStyle: "italic" }}
                  >
                    No KYC documents uploaded yet.
                  </TableCell>
                </TableRow>
              ) : (
                currentDocs.map((row, idx) => (
                  <TableRow key={row.id}>
                    {/* Sr. No */}
                    <TableCell>{startIndex + idx + 1}</TableCell>
                    {/* Type — kyc_type from the parent record */}
                    <TableCell>{capitalizeFirst(row.kycType) || "—"}</TableCell>

                    {/* Document — the document type name e.g. "Aadhar" */}
                    <TableCell>{row.documentType}</TableCell>

                    {/* File — View button */}
                    <TableCell>
                      {row.documentUrl ? (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => window.open(row.documentUrl, "_blank")}
                          sx={{
                            textTransform: "none",
                            borderRadius: "20px",
                            px: 2,
                            py: 0.5,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            bgcolor: "scraptradeYellow.main",
                            color: "#000",
                            "&:hover": { bgcolor: "scraptradeYellow.dark" },
                          }}
                        >
                          View
                        </Button>
                      ) : (
                        "—"
                      )}
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={capitalizeFirst(row.status)}
                        color={getStatusChipColor(row.status)}
                        size="small"
                      />
                    </TableCell>

                    {/* Uploaded */}
                    <TableCell>{timeAgo(row.created_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mt: 2,
          gap: 2,
        }}
      >
        <Typography variant="body2">
          Showing {startIndex + 1} to {Math.min(endIndex, filteredDocs.length)}{" "}
          of {filteredDocs.length} entries
        </Typography>
        <CustomPagination />
      </Box>

      <KycUploadModal
        open={openKYCModal}
        onClose={() => setOpenKYCModal(false)}
        onSave={handleSaveKYC}
      />
    </Box>
  );
};

export default KycVerificationTab;
